/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPicker" />
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { Selection } from "OfficeFabric/Selection";
import * as React from "react";
import { equals, localeFormat } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { getRepositoryZeroDataText, updateSelectedRepos } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { IAddRepoPickerProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPicker.Props";
import {
    IRepositorySelectionProps,
    RepositorySelection as GitHubEnterpriseRepositorySelection,
    RepositorySelection as GitHubRepositorySelection
} from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/RepositorySelection";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

class GitHubAdminRepositorySelection extends GitHubRepositorySelection {
    constructor(props: IRepositorySelectionProps, context: IVssComponentContext) {
        super(props, context);
    }
}

class GitHubEnterpriseAdminRepositorySelection extends GitHubEnterpriseRepositorySelection {
    constructor(props: IRepositorySelectionProps, context: IVssComponentContext) {
        super(props, context);
    }

    protected autoSelectConnection(): boolean {
        return false;
    }
}

export interface IAddRepoPickerState {
    selectedRepos: { [repoNodeId: string]: ExternalGitRepo };
    numberOfReposInView: number;
    numberOfReposTotal: number;
    filterText?: string;
}

export class AddRepoPicker extends VssComponent<IAddRepoPickerProps, IAddRepoPickerState> {
    constructor(props: IAddRepoPickerProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            selectedRepos: {},
            numberOfReposInView: 0,
            numberOfReposTotal: 0
        };
    }

    public render() {
        const isGitHub = equals(this.props.repositoryType, RepositoryTypes.GitHub, true);
        const strongBoxKey = this.props.isGitHubApp ? GitHubConnectionConstants.UseWellKnownStrongBoxLocationKey : undefined;

        const props: IRepositorySelectionProps = {
            connectionId: !this.props.isGitHubApp ? this.props.serviceEndpointId : undefined!,
            strongBoxKey: strongBoxKey,
            serviceName: isGitHub ? Resources.GitHub : Resources.GitHubEnterprise,
            sourceProvider: this.props.repositoryType,
            additionalRepositoryFilter: this._additionalRepositoryFilter,
            onSelectedRepositoriesChanged: this._onSelectedRepositoriesChanged,
            onRenderRepositoryListHeader: this._renderRepositoryListHeader,
            onRepositoryListUpdate: this._onRepositoryListUpdate,
            emptyRepositoryInfoText: getRepositoryZeroDataText(this.props.repositoryType, this.props.isGitHubApp),
            emptyRepositorySeverityLevel: this.props.isGitHubApp ? MessageCardSeverity.Error : MessageCardSeverity.Warning,
            onError: this.props.onError,
            shouldPreselectRepo: this.props.shouldPreselectRepo,
            orgOrUserName: this.props.orgOrUserName,
            isUserType: this.props.isUserType
        };

        return (
            <div className="add-repo-picker">
                {isGitHub ? <GitHubAdminRepositorySelection {...props} /> : <GitHubEnterpriseAdminRepositorySelection {...props} />}
            </div>
        );
    }

    private readonly _additionalRepositoryFilter = (repo: ExternalGitRepo): boolean => {
        // If GitHub App, only require read permission since App will never have admin permission
        // For unified experience, we need admin permission to install the app
        if (
            !repo.additionalProperties.archived && // Repo is not archived (archived repo is read only)
            ((this.props.isGitHubApp && !this.props.isUnifiedGitHubApp) || repo.additionalProperties.hasAdminPermission)
        ) {
            const { selectedReposFromSameProvider } = this.props;
            if (selectedReposFromSameProvider) {
                return !selectedReposFromSameProvider.some(existingRepo => existingRepo.id === repo.additionalProperties.nodeId);
            } else {
                // We need to return true here as we don't have existing repos to compare with
                return true;
            }
        } else {
            return false;
        }
    };

    private readonly _renderRepositoryListHeader = (): JSX.Element | null => {
        const { numberOfReposInView, numberOfReposTotal, filterText } = this.state;
        if (numberOfReposTotal > 0) {
            const numberOfReposSelected = Object.keys(this.state.selectedRepos).length;
            const isFilterApplied = !!filterText;

            let infoText: string;
            if (isFilterApplied) {
                infoText = localeFormat(
                    Resources.RepositoryPickerViewStatus_Filtered,
                    numberOfReposInView,
                    numberOfReposTotal,
                    numberOfReposSelected
                );
            } else {
                infoText = localeFormat(Resources.RepositoryPickerViewStatus_NotFiltered, numberOfReposInView, numberOfReposSelected);
            }

            return <div className="info-text">{infoText}</div>;
        }

        return null;
    };

    private readonly _onRepositoryListUpdate = (action: string, repoCount: number, filterText?: string, selection?: Selection): void => {
        const isApplyFilterTextAction = action === "ReposFilter";
        const isFilterApplied = !!filterText;
        const numberOfReposInView = isApplyFilterTextAction || !isFilterApplied ? repoCount : this.state.numberOfReposInView;
        const numberOfReposTotal = isApplyFilterTextAction ? this.state.numberOfReposTotal : repoCount;
        this.setState({
            numberOfReposInView: numberOfReposInView,
            numberOfReposTotal: numberOfReposTotal,
            filterText: filterText
        });

        if (selection) {
            requestAnimationFrame(() => {
                // Fix the selection when the list is updated (filtered)
                const listedRepos = selection.getItems() as ExternalGitRepo[];
                const { selectedRepos } = this.state;
                for (let index = 0; index < listedRepos.length; index++) {
                    const repo = listedRepos[index];
                    const repoNodeId = repo.additionalProperties.nodeId;
                    selection.setIndexSelected(index, !!selectedRepos[repoNodeId], false);
                }
            });
        }
    };

    private readonly _onSelectedRepositoriesChanged = (repositories: ExternalGitRepo[], filterText?: string, selection?: Selection): void => {
        // Get updated list of selected repos
        const selectedRepos = getSelectedRepos(this.state, repositories, filterText, selection);

        if (!selectedRepos) {
            return;
        }

        this.setState({
            selectedRepos: selectedRepos
        });

        // Invoke callback
        const { onAddReposSelectionChanged } = this.props;
        if (onAddReposSelectionChanged) {
            const repoNodeIds = Object.keys(selectedRepos) || [];
            onAddReposSelectionChanged(repoNodeIds.map(id => selectedRepos[id]));
        }
    };
}

/**
 * Utility function to update the repo selection based on the input and current state.
 * NOTE: This function is exported for unit tests
 */
export function getSelectedRepos(
    currentState: IAddRepoPickerState,
    selectedReposFromUI: ExternalGitRepo[],
    filterText?: string,
    selection?: Selection
): { [repoNodeId: string]: ExternalGitRepo } | undefined {
    if (currentState.filterText !== filterText) {
        // No op if filter text changed.
        // When filter text applied, selection change event will trigger but no real user selection input.
        return undefined;
    }

    // Copy the current selected repos from the state.
    let selectedRepos = { ...currentState.selectedRepos };

    if (selection) {
        const listedRepos = (selection.getItems() as ExternalGitRepo[]).map(repo => {
            const repoNodeId = repo.additionalProperties.nodeId;
            repo.additionalProperties[GitHubConnectionConstants.RestApiRepoId] = repo.id;
            return {
                id: repoNodeId,
                name: repo.additionalProperties.repoNameWithOwner
            } as ExternalGitRepo;
        });
        updateSelectedRepos(listedRepos, selectedRepos, selection);
    } else {
        // If selection is not available, reset the selected repos with the input.
        selectedRepos = {};
        (selectedReposFromUI || []).forEach(repo => {
            const repoNodeId = repo.additionalProperties.nodeId;
            selectedRepos[repoNodeId] = {
                id: repoNodeId,
                name: repo.additionalProperties.repoNameWithOwner
            } as ExternalGitRepo;
        });
    }

    return selectedRepos;
}
